import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from 'services/queryClient';
import { ReadyStateProvider } from 'contexts/ReadyStateContext';
import { InitialDataProvider } from 'contexts/InitialDataContext';
import { LanguageProvider } from 'contexts/LanguageContext';
import { AuthProvider, ProtectedRoute, LoginPage, AccessDeniedPage } from 'features/auth';
import { VoicesListView } from 'features/generation-lab/views/VoicesListView';
import { VoiceProvidersView } from 'features/generation-lab/views/VoiceProvidersView';
import { CustomVoiceTestView } from 'features/generation-lab/views/CustomVoiceTestView';
import { EpisodesListView } from 'features/generation-lab/views/EpisodesListView';
import { EpisodeFormView } from 'features/generation-lab/views/EpisodeFormView';
import { EpisodeTryView } from 'features/generation-lab/views/EpisodeTryView';
import { VoiceAssignmentView } from 'features/generation-lab/views/VoiceAssignmentView';
import { DisplayLocalizationsView } from 'features/generation-lab/views/DisplayLocalizationsView';
import { EpisodeSessionsView } from 'features/generation-lab/views/EpisodeSessionsView';
import { EvalEpisodeTab } from 'features/generation-lab/views/EvalEpisodeTab';
import { EpisodeJudgeEvaluationView } from 'features/generation-lab/views/EpisodeJudgeEvaluationView';
import { EvalPersonasView } from 'features/generation-lab/views/EvalPersonasView';
import { EpisodeSelectorView } from 'features/generation-lab/views/EpisodeSelectorView';
import { GenerateEpisodeView } from 'features/generation-lab/views/GenerateEpisodeView';
import { IdeaGeneratorView } from 'features/generation-lab/views/IdeaGeneratorView';
import { BulkEpisodeGenerationView } from 'features/generation-lab/views/BulkEpisodeGenerationView';
import { PromptsListView, PromptEditView } from 'features/prompts';
import {
  LanguagesListView,
  LocalesListView,
  TagsListView,
  LearningGoalsListView,
  SkillLevelsListView,
  EpisodeDurationsListView,
  NotificationTemplatesListView,
  LegalDocumentsListView,
} from 'features/dictionaries';
import { UsersPage, UserDetailView } from 'features/user-management';
import {
  AllWordsView,
  WordListsListView,
  WordListFormView,
  WordListWordsView,
  WordFormView,
  IdeasReviewView,
  GenerateWordListView,
} from 'features/word-lists';
import { ProviderCredentialsListView } from 'features/provider-credentials';
import { TutorsListView } from 'features/tutors';
import { DashboardLayout } from 'layouts/DashboardLayout';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ReadyStateProvider>
        <InitialDataProvider>
          <LanguageProvider>
            <AuthProvider>
              <BrowserRouter>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/access-denied" element={<AccessDeniedPage />} />

                  {/* Protected routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/users" replace />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="users/:userId" element={<UserDetailView />} />
                    <Route path="episodes" element={<EpisodesListView />} />
                    <Route path="episodes/generate" element={<GenerateEpisodeView />} />
                    <Route path="episodes/ideas" element={<IdeaGeneratorView />} />
                    <Route path="episodes/bulk-generate" element={<BulkEpisodeGenerationView />} />
                    <Route path="episodes/eval" element={<EpisodeSelectorView />} />
                    <Route path="episodes/eval/:episodeId" element={<EvalEpisodeTab />} />
                    <Route
                      path="episodes/eval/:episodeId/judge"
                      element={<EpisodeJudgeEvaluationView />}
                    />
                    <Route path="episodes/personas" element={<EvalPersonasView />} />
                    <Route path="episodes/new" element={<EpisodeFormView />} />
                    <Route path="episodes/:id/edit" element={<EpisodeFormView />} />
                    <Route path="sessions/:sessionId" element={<EpisodeTryView />} />
                    <Route path="episodes/:episodeId/voices" element={<VoiceAssignmentView />} />
                    <Route
                      path="episodes/:episodeId/localizations"
                      element={<DisplayLocalizationsView />}
                    />
                    <Route path="episodes/:episodeId/sessions" element={<EpisodeSessionsView />} />
                    <Route path="voices" element={<VoicesListView />} />
                    <Route path="voice-providers" element={<VoiceProvidersView />} />
                    <Route path="voice-test" element={<CustomVoiceTestView />} />
                    <Route path="provider-credentials" element={<ProviderCredentialsListView />} />
                    <Route path="prompts" element={<PromptsListView />} />
                    <Route path="prompts/:promptKey/edit" element={<PromptEditView />} />
                    {/* Words routes (All Words and Word Lists) */}
                    <Route path="words" element={<AllWordsView />} />
                    <Route path="words/new" element={<WordFormView />} />
                    <Route path="words/:word/:lang/edit" element={<WordFormView />} />
                    <Route path="words/lists" element={<WordListsListView />} />
                    <Route path="words/lists/ideas" element={<IdeasReviewView />} />
                    <Route path="words/lists/generate" element={<GenerateWordListView />} />
                    <Route path="words/lists/new" element={<WordListFormView />} />
                    <Route path="words/lists/:id/edit" element={<WordListFormView />} />
                    <Route path="words/lists/:listId/words" element={<WordListWordsView />} />
                    {/* Legacy word-lists routes (redirect to new structure) */}
                    <Route path="word-lists" element={<Navigate to="/words/lists" replace />} />
                    <Route path="word-lists/*" element={<Navigate to="/words/lists" replace />} />
                    {/* Dictionaries routes */}
                    <Route path="dictionaries">
                      <Route index element={<Navigate to="/dictionaries/languages" replace />} />
                      <Route path="languages" element={<LanguagesListView />} />
                      <Route path="tutors" element={<TutorsListView />} />
                      <Route path="locales" element={<LocalesListView />} />
                      <Route path="tags" element={<TagsListView />} />
                      <Route path="learning-goals" element={<LearningGoalsListView />} />
                      <Route path="skill-levels" element={<SkillLevelsListView />} />
                      <Route path="episode-durations" element={<EpisodeDurationsListView />} />
                      <Route
                        path="notification-templates"
                        element={<NotificationTemplatesListView />}
                      />
                      <Route path="legal-documents" element={<LegalDocumentsListView />} />
                    </Route>
                    {/* Redirect old generation-lab route to new location */}
                    <Route
                      path="generation-lab"
                      element={<Navigate to="/episodes/ideas" replace />}
                    />
                  </Route>

                  {/* Catch-all redirect */}
                  <Route path="*" element={<Navigate to="/users" replace />} />
                </Routes>
                <Toaster position="top-right" />
              </BrowserRouter>
            </AuthProvider>
          </LanguageProvider>
        </InitialDataProvider>
      </ReadyStateProvider>
    </QueryClientProvider>
  );
}

export default App;
